import React from 'react';
import { Link } from 'react-router-dom';
import '../../../global.css';
import Navbar from '../../../components/Navbar';
import SiteFooter from '../../../components/SiteFooter';
import NeuralCanvas from '../../../components/NeuralCanvas';
import { useSEO } from '../../../hooks/useSEO';
import safetyHero from '../../../assets/images/solar.jpeg'; // Import your local image

export default function Energy() {
  useSEO({
    title: 'Energy & Grid AI for Africa',
    description: 'Keep the lights on — AI that predicts grid faults before they happen, optimizes solar systems, and intelligently manages power distribution across African infrastructure.',
    path: '/solutions/energy',
  });
  return (
    <>
      <NeuralCanvas />
      <div className="neural-page">
      <Navbar />

      <div style={{ borderBottom: '1px solid var(--line)', padding: '80px 0 72px' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>SOLUTION · ENERGY & POWER</div>
          <div className="sol-hero-grid">
            <div>
              <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 400, letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 20 }}>
                Power that's<br /><span style={{ color: 'var(--fg-mid)', fontWeight: 300 }}>always there.</span>
              </h1>
              <p style={{ fontSize: 16, color: 'var(--fg-mid)', lineHeight: 1.7, maxWidth: 480, marginBottom: 32 }}>
                Unreliable electricity costs African businesses billions every year. Cybernate monitors grids,
                predicts failures before they cause outages, and optimizes solar systems — keeping the lights on,
                from Lagos to Lusaka.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <Link to="/signup" className="btn btn-primary">Get started →</Link>
                <Link to="/developers" className="btn btn-ghost">Developer platform</Link>
              </div>
            </div>
            <div style={{ width: '100%', height: 'clamp(260px, 40vw, 480px)', borderRadius: 'var(--r-lg)', overflow: 'hidden', border: '1px solid var(--line)' }}>
              <img
                src={safetyHero}
                alt="Solar panels and energy grid infrastructure in Africa"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--line)', padding: '40px 0', background: 'var(--bg-1)' }}>
        <div className="sol-stats-grid" style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          {[['72h', 'Solar forecast horizon'], ['91%', 'Grid fault detection accuracy'], ['220kW', 'Max array size tested'], ['12', 'African climate zones covered']].map(([n, l], i) => (
            <div key={n} style={{ padding: '24px 32px', borderRight: i < 3 ? '1px solid var(--line)' : 'none' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 500, letterSpacing: '-0.04em', color: 'var(--fg)', marginBottom: 6 }}>{n}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-dim)' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          PHOTO SECTION — uncomment when image is ready.
          Replace src with your Unsplash URL.
          Tip: append ?w=1400&q=85&fit=crop to any Unsplash URL.
          ═══════════════════════════════════════════════════ */}
      {/*
      <div style={{ padding: '56px 0 0' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{
            width: '100%',
            height: 'clamp(220px, 42vw, 480px)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden',
            border: '1px solid var(--line)',
            background: 'var(--bg-1)',
          }}>
            <img
              src="YOUR_IMAGE_URL_HERE"
              alt="Solar panels and energy grid infrastructure in Africa"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
              loading="lazy"
            />
          </div>
        </div>
      </div>
      */}

      <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '72px var(--pad-x)' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>What you can build</div>
        <h2 style={{ fontSize: 32, fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 48 }}>Energy intelligence<br /><span style={{ color: 'var(--fg-mid)', fontWeight: 300 }}>for the continent.</span></h2>
        <div className="sol-features-grid">
          {[
            { title: 'Solar yield forecasting', desc: 'Predict solar production for mini-grids and off-grid arrays across all African climate zones, 72 hours ahead.' },
            { title: 'Grid fault detection', desc: 'Identify transformer failures, line faults, and overloads from sensor data. Alert operators before outages occur.' },
            { title: 'Demand forecasting', desc: 'Model energy demand patterns for African cities and rural areas — accounting for local usage patterns, market days, religious calendars.' },
            { title: 'Maintenance scheduling', desc: 'Predict optimal maintenance windows for generation assets. Minimize downtime while reducing maintenance costs.' },
            { title: 'Load optimization', desc: 'Balance supply and demand across distributed micro-grids. Smart load shedding with community notification in local languages.' },
            { title: 'Climate risk analysis', desc: 'Assess infrastructure exposure to extreme weather events — floods, droughts, dust storms — with location-specific risk scores.' },
          ].map(u => (
            <div key={u.title} style={{ padding: '24px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg)', marginBottom: 8 }}>{u.title}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.65 }}>{u.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--line)', padding: '64px 0', background: 'var(--bg-1)' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '0 var(--pad-x)' }}>
          <h2 style={{ fontSize: 28, fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 12 }}>Keep Africa's lights on, intelligently.</h2>
          <p style={{ fontSize: 14, color: 'var(--fg-mid)', marginBottom: 28 }}>Free to start. Works across all African climate zones. No credit card required.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link to="/signup" className="btn btn-primary btn-lg">Get started →</Link>
            <Link to="/developers" className="btn btn-ghost btn-lg">Developer docs</Link>
          </div>
        </div>
      </div>

      <SiteFooter />
      </div>
    </>
  );
}
